import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuthenticatedFetch from "../../auth/authenticated";

interface ClientRow {
  id: string;
  short_name: string;
  api_intake_enabled?: boolean;
  azure_app_client_id?: string | null;
  secret_created_at?: string | null;
  secret_expires_at?: string | null;
}

interface ApiIntakeModalProps {
  open: boolean;
  onClose: () => void;
  client: ClientRow;
  /** Called after a successful enable/disable/rotate so the parent table
   *  can refetch and reflect the new state. */
  onChanged?: () => void;
}

interface SecretPayload {
  azure_app_client_id?: string;
  secret: string;
  secret_created_at: string;
  secret_expires_at: string;
}

const PROVISION_STEP_KEYS = [
  "create_application",
  "service_principal",
  "assign_role",
  "client_credential",
] as const;

type ProvisionStepKey = (typeof PROVISION_STEP_KEYS)[number];

const STEP_I18N: Record<ProvisionStepKey, string> = {
  create_application: "API_INTAKE.STEP_CREATE_APPLICATION",
  service_principal: "API_INTAKE.STEP_SERVICE_PRINCIPAL",
  assign_role: "API_INTAKE.STEP_ASSIGN_ROLE",
  client_credential: "API_INTAKE.STEP_CREATE_SECRET",
};

const DAY_MS = 24 * 60 * 60 * 1000;

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.floor((new Date(iso).getTime() - Date.now()) / DAY_MS);
}

function parseErrorDetail(body: string): {
  message: string;
  failedStepKey?: ProvisionStepKey;
} {
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    const rawDetail = parsed.detail;
    const detail: Record<string, unknown> =
      typeof rawDetail === "object" && rawDetail !== null
        ? (rawDetail as Record<string, unknown>)
        : parsed;
    const message =
      typeof detail.error === "string"
        ? detail.error
        : typeof rawDetail === "string"
          ? rawDetail
          : body;
    const key = detail.failed_step_key;
    const failedStepKey =
      typeof key === "string" && PROVISION_STEP_KEYS.includes(key as ProvisionStepKey)
        ? (key as ProvisionStepKey)
        : undefined;
    return { message, failedStepKey };
  } catch {
    return { message: body };
  }
}

function ExpiryBadge({ expiresAt }: { expiresAt: string | null | undefined }) {
  const { t } = useTranslation();
  const days = daysUntil(expiresAt);
  if (days === null) return null;
  if (days < 0) {
    return <Chip color="error" size="small" label={t("API_INTAKE.EXPIRED")} />;
  }
  if (days <= 7) {
    return <Chip color="error" size="small" label={t("API_INTAKE.EXPIRES_DAYS", { days })} />;
  }
  if (days <= 30) {
    return <Chip color="warning" size="small" label={t("API_INTAKE.EXPIRES_DAYS", { days })} />;
  }
  return <Chip color="default" size="small" label={t("API_INTAKE.EXPIRES_DAYS", { days })} />;
}

function CopyableSecret({ value }: { value: string }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      type={visible ? "text" : "password"}
      inputProps={{ readOnly: true, "aria-label": t("API_INTAKE.SECRET_VALUE") }}
      sx={{
        "& .MuiInputBase-input": {
          color: "#000",
          fontFamily: "monospace",
          wordBreak: "break-all",
        },
        "& .MuiOutlinedInput-root": {
          bgcolor: "#f5f5f5",
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? t("API_INTAKE.HIDE_SECRET") : t("API_INTAKE.SHOW_SECRET")}
            >
              {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={async () => {
                await navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              title={copied ? t("API_INTAKE.COPIED") : t("API_INTAKE.COPY")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

const ApiIntakeModal: React.FC<ApiIntakeModalProps> = ({ open, onClose, client, onChanged }) => {
  const { t } = useTranslation();
  const getAccessHeader = useAuthenticatedFetch();

  const [busy, setBusy] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [failedStepKey, setFailedStepKey] = useState<ProvisionStepKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<SecretPayload | null>(null);
  const [current, setCurrent] = useState<ClientRow>(client);

  useEffect(() => {
    let cancelled = false;

    setRevealedSecret(null);
    setError(null);
    setFailedStepKey(null);
    setCurrent(client);

    async function loadClientDetails() {
      setLoadingDetails(true);
      try {
        const headers = await getAccessHeader(false);
        const response = await fetch(`/api/clients/${client.id}`, {
          method: "GET",
          headers,
        });
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        const detail = (await response.json()) as ClientRow;
        if (!cancelled) {
          setCurrent({ ...client, ...detail });
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
        }
      } finally {
        if (!cancelled) {
          setLoadingDetails(false);
        }
      }
    }

    loadClientDetails();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when opening for a different client
  }, [client.id]);

  const enabled = Boolean(current.api_intake_enabled);

  async function call(method: "POST" | "DELETE", path: string): Promise<SecretPayload | null> {
    setBusy(true);
    setError(null);
    setFailedStepKey(null);
    try {
      const headers = await getAccessHeader();
      const response = await fetch(`/api/clients/${client.id}${path}`, { method, headers });
      if (!response.ok) {
        const body = await response.text();
        const { message, failedStepKey: stepKey } = parseErrorDetail(body);
        if (stepKey) setFailedStepKey(stepKey);
        throw new Error(message || `${response.status} ${response.statusText}`);
      }
      const body = await response.json();
      return method === "DELETE" ? null : (body as SecretPayload);
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function handleEnable() {
    const payload = await call("POST", "/api-intake/enable");
    if (payload) {
      setRevealedSecret(payload);
      setCurrent({
        ...current,
        api_intake_enabled: true,
        azure_app_client_id: payload.azure_app_client_id || null,
        secret_created_at: payload.secret_created_at,
        secret_expires_at: payload.secret_expires_at,
      });
      onChanged?.();
    }
  }

  async function handleDisable() {
    const ok = (await call("DELETE", "/api-intake")) === null && !error;
    if (ok) {
      setRevealedSecret(null);
      setCurrent({
        ...current,
        api_intake_enabled: false,
        azure_app_client_id: null,
        secret_created_at: null,
        secret_expires_at: null,
      });
      onChanged?.();
    }
  }

  async function handleRotate() {
    const payload = await call("POST", "/api-intake/rotate-secret");
    if (payload) {
      setRevealedSecret(payload);
      setCurrent({
        ...current,
        secret_created_at: payload.secret_created_at,
        secret_expires_at: payload.secret_expires_at,
      });
      onChanged?.();
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("API_INTAKE.TITLE", { name: current.short_name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("API_INTAKE.DESCRIPTION")}
          </Typography>

          {busy && !enabled && !loadingDetails && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t("API_INTAKE.PROGRESS_TITLE")}
              </Typography>
            </Stack>
          )}

          {loadingDetails && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loadingDetails && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("API_INTAKE.STATUS")}
                </Typography>
                <Box>
                  {enabled ? (
                    <Chip color="success" size="small" label={t("API_INTAKE.ENABLED")} />
                  ) : (
                    <Chip size="small" label={t("API_INTAKE.DISABLED")} />
                  )}
                </Box>
              </Box>

              {enabled && (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("API_INTAKE.CLIENT_ID")}
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {current.azure_app_client_id || "—"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("API_INTAKE.SECRET_DATES")}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {current.secret_created_at
                          ? new Date(current.secret_created_at).toLocaleString()
                          : "—"}
                        {" → "}
                        {current.secret_expires_at
                          ? new Date(current.secret_expires_at).toLocaleString()
                          : "—"}
                      </Typography>
                      <ExpiryBadge expiresAt={current.secret_expires_at} />
                    </Stack>
                  </Box>
                </>
              )}
            </>
          )}

          {revealedSecret && (
            <Alert severity="warning" icon={<KeyIcon />}>
              <Typography variant="subtitle2">{t("API_INTAKE.SECRET_REVEAL_TITLE")}</Typography>
              <Typography variant="body2" gutterBottom>
                {t("API_INTAKE.SECRET_REVEAL_DESCRIPTION")}
              </Typography>
              <CopyableSecret value={revealedSecret.secret} />
            </Alert>
          )}

          {error && (
            <Alert severity="error">
              {failedStepKey
                ? t("API_INTAKE.FAILED_AT_STEP", { label: t(STEP_I18N[failedStepKey]) })
                : null}
              {failedStepKey && <br />}
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("API_INTAKE.CLOSE")}
        </Button>
        {!enabled && (
          <Button variant="contained" onClick={handleEnable} disabled={busy || loadingDetails}>
            {busy ? <CircularProgress size={18} /> : t("API_INTAKE.ENABLE")}
          </Button>
        )}
        {enabled && (
          <>
            <Button color="error" onClick={handleDisable} disabled={busy || loadingDetails}>
              {t("API_INTAKE.DISABLE")}
            </Button>
            <Button variant="contained" onClick={handleRotate} disabled={busy || loadingDetails}>
              {busy ? <CircularProgress size={18} /> : t("API_INTAKE.ROTATE_SECRET")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApiIntakeModal;
